import { gql } from '@apollo/client';
import { AlertDescription, AlertTitle, Box, Button, useDisclosure } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import UpgradeToWaveEssentialModal from '~components/organization/UpgradeToWaveEssentialModal/UpgradeToWaveEssentialModal';
import { CreateScheduleModal } from '~components/schedules/CreateScheduleModal';
import { EmptySchedules } from '~components/schedules/EmptySchedules';
import { ScheduleTable, useScheduleTable } from '~components/schedules/ScheduleTable/ScheduleTable';
import { WarningAlert } from '~components/ui/Alert';
import { Page } from '~components/ui/Page';
import { PageContent } from '~components/ui/PageContent';
import { PageHeading } from '~components/ui/PageHeading';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { HandleApiError } from '~graphql/HandleApiError';
import { useAnalyticsReporter } from '~utils/analytics';
import { FeatureFlag, useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { ensure } from '~utils/types';
import { useLimits } from '~utils/useLimits';
import { useCustomerSchedulesPageQuery } from './__generated__/index.graphql';

const POLL_INTERVAL = 5000;

export function CustomerSchedulesPage() {
  const { customer } = useParams();
  const navigate = useNavigate();
  const { verifyUserPermissions } = useAuth();

  const customerHandle = ensure(customer);
  const { data, loading, error, refetch, startPolling, stopPolling } =
    useCustomerSchedulesPageQuery({
      variables: { customerHandle },
      pollInterval: POLL_INTERVAL,
    });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  const analytics = useAnalyticsReporter();
  const table = useScheduleTable(data?.customer?.schedules ?? []);

  const createScheduleModal = useDisclosure();

  const handleScheduleClicked = useCallback((id: string) => navigate(id), [navigate]);

  const handleCreate = useCallback(() => {
    analytics.track('scheduleCreateStart');
    createScheduleModal.onOpen();
  }, [analytics, createScheduleModal]);

  const handleCreateSuccess = useCallback(() => {
    analytics.track('scheduleCreateComplete');
    refetch({ customerHandle });
    createScheduleModal.onClose();
  }, [analytics, createScheduleModal, customerHandle, refetch]);

  const {
    schedules: {
      schedulesNumberPerCustomerLimit: schedulesLimit,
      isSchedulesLimitPerCustomerReached,
    },
  } = useLimits();

  const { hasWaveSubscription: hasEssentialSubscription } = useSubscriptionType(data?.customer);

  const isSchedulesLimitReached = isSchedulesLimitPerCustomerReached(
    data?.customer?.schedules,
    hasEssentialSubscription,
  );

  const hasScheduleCreatePermission = verifyUserPermissions([Permission.PowerscheduleCreate]);

  const canCreateSchedule = hasScheduleCreatePermission && !isSchedulesLimitReached;

  const shouldRenderUpgradeToWaveEssentialModal =
    !hasEssentialSubscription && isSchedulesLimitReached;

  const hasSchedules = (data?.customer?.schedules.length ?? 0) > 0;

  return (
    <Page title="Schedules" pageName="schedules_overview">
      <PageContent>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : (
          data &&
          data.customer && (
            <>
              <PageHeading
                actions={
                  <Button
                    variant="solid"
                    colorScheme="blue"
                    onClick={handleCreate}
                    isDisabled={!canCreateSchedule}
                  >
                    {t('createSchedule')}
                  </Button>
                }
                marginBottom={isSchedulesLimitReached ? '8' : undefined}
              >
                {t('schedules')}
              </PageHeading>
              {isSchedulesLimitReached ? (
                <WarningAlert marginBottom="6">
                  <AlertTitle>{t('schedulesLimitReached')}</AlertTitle>
                  <AlertDescription>
                    {isSubscriptionsEnabled && !hasEssentialSubscription
                      ? t('schedulesLimitReachedAlert')
                      : t('schedulesLimitMessage', { schedulesLimit })}
                  </AlertDescription>
                </WarningAlert>
              ) : undefined}
              <Box
                boxShadow="elevated"
                border="1px solid"
                borderRadius="md"
                borderColor="gray.100"
                overflowX="auto"
                background="white"
              >
                {hasSchedules ? (
                  <ScheduleTable table={table} onGoToDetail={handleScheduleClicked} />
                ) : (
                  <EmptySchedules handleCreate={handleCreate} />
                )}
              </Box>
              {shouldRenderUpgradeToWaveEssentialModal && (
                <FeatureFlag feature="subscriptions">
                  <Box
                    m="auto"
                    marginY="16"
                    maxW="50VW"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="gray.100"
                    overflow="hidden"
                    bg="white"
                    boxShadow="elevated"
                  >
                    <UpgradeToWaveEssentialModal.EmptyView />
                  </Box>
                </FeatureFlag>
              )}
              <CreateScheduleModal
                customerId={data.customer.id}
                isOpen={createScheduleModal.isOpen}
                onCancel={createScheduleModal.onClose}
                onSuccess={handleCreateSuccess}
              />
            </>
          )
        )}
      </PageContent>
    </Page>
  );
}

CustomerSchedulesPage.graphql = {
  queries: {
    CustomerSchedulesPage: gql`
      query CustomerSchedulesPage($customerHandle: String!) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          schedules {
            ...ScheduleTable_schedule
          }
          ...CustomerSubscriptionData_customer
        }
      }
    `,
  },
};
